export interface SnapshotCacheGeneration {
    global: number;
    scope: number;
}

interface SnapshotCacheEntry<T> {
    scopeKey: string;
    expiresAtMs: number;
    generation: SnapshotCacheGeneration;
    value: T;
}

/**
 * A single-entry cache that is isolated by authenticated subject and refuses
 * writes from requests that started before an invalidation. The generation
 * check matters when a slow entitlement response finishes after a mutation or
 * account switch has already requested authoritative state.
 */
export class GenerationSafeSnapshotCache<T> {
    private entry: SnapshotCacheEntry<T> | null = null;
    private globalGeneration = 0;
    private readonly scopeGenerations = new Map<string, number>();

    captureGeneration(scopeKey: string): SnapshotCacheGeneration {
        return {
            global: this.globalGeneration,
            scope: this.scopeGenerations.get(scopeKey) ?? 0,
        };
    }

    isCurrent(scopeKey: string, generation: SnapshotCacheGeneration): boolean {
        const current = this.captureGeneration(scopeKey);
        return current.global === generation.global && current.scope === generation.scope;
    }

    read(scopeKey: string, nowMs = Date.now()): T | null {
        if (
            !this.entry
            || this.entry.scopeKey !== scopeKey
            || this.entry.expiresAtMs <= nowMs
            || !this.isCurrent(scopeKey, this.entry.generation)
        ) {
            return null;
        }

        return this.entry.value;
    }

    write(
        scopeKey: string,
        value: T,
        expiresAtMs: number,
        generation: SnapshotCacheGeneration
    ): boolean {
        if (!this.isCurrent(scopeKey, generation)) {
            return false;
        }

        this.entry = {
            scopeKey,
            expiresAtMs,
            generation,
            value,
        };
        return true;
    }

    invalidate(scopeKey?: string): void {
        if (scopeKey) {
            this.scopeGenerations.set(
                scopeKey,
                (this.scopeGenerations.get(scopeKey) ?? 0) + 1
            );
            if (this.entry?.scopeKey === scopeKey) {
                this.entry = null;
            }
            return;
        }

        this.globalGeneration += 1;
        this.entry = null;
    }
}
